import { Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { Idea, IdeaInputParams } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { NgbActiveModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { BusService } from '@services/bus.service';
import { EventsService } from '@services/events.service';
import { PodcastService } from '@services/sessions/podcast.service';
import { map, mergeMap, Observable, throwError } from 'rxjs';

import {
  InputFormComponent,
  InputType,
} from './input-form/input-form.component';
import { OutlineFormComponent } from './outline-form/outline-form.component';

enum Steps {
  Input,
  Outline,
  GeneratingScript,
}

@Component({
  selector: 'app-new-idea-modal',
  standalone: true,
  imports: [NgbModalModule, InputFormComponent, OutlineFormComponent],
  providers: [PodcastService],
  templateUrl: './new-idea-modal.component.html',
  styleUrl: './new-idea-modal.component.scss',
})
export class NewIdeaModalComponent {
  @ViewChild(InputFormComponent) inputForm!: InputFormComponent;
  @ViewChild(OutlineFormComponent) outlineForm: OutlineFormComponent | null;

  activeModal = inject(NgbActiveModal);
  session: Session;
  step = Steps.Input;
  Steps = Steps;
  serverError = false;
  featureFlags = environment.featureFlags;

  private podcastService = inject(PodcastService);
  private destroyRef = inject(DestroyRef);
  private busService = inject(BusService);
  private eventsService = inject(EventsService);
  private router = inject(Router);
  private podcastTitle: string;
  private podcastName: string;

  goToOutlineStep() {
    this.step = Steps.Outline;
  }

  goToScript() {
    if (this.inputForm.form.invalid) {
      this.inputForm.form.markAllAsTouched();
      return;
    }

    this.step = Steps.GeneratingScript;
    this.serverError = false;

    this.generateScript()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap(data =>
          this.savePodcast(data).pipe(
            map(idea => [idea, data.inconsistency] as [Idea, string]),
          ),
        ),
      )
      .subscribe({
        next: ([createdIdea, inconsistency]) => {
          this.activeModal.close();
          this.router.navigate(
            ['topics', this.session._id, 'ideas', createdIdea._id],
            {
              queryParams: { edit: true },
              state: { inconsistency },
            },
          );
        },
        error: () => {
          this.step = Steps.Input;
          this.serverError = true;
        },
      });
  }

  private generateScript(): Observable<{
    inputParams: IdeaInputParams;
    script: string;
    shortTitle?: string;
    oneLiner?: string;
    inconsistency?: string;
  }> {
    const {
      podcastName,
      guests: numberOfGuests,
      sections: numberOfSections,
      provideContext,
      context,
      inputType,
      topic,
      text,
      sources,
    } = this.inputForm.form.getRawValue();
    this.podcastName = podcastName;

    switch (inputType) {
      case InputType.Topic: {
        this.podcastTitle = topic.name;
        return this.podcastService
          .generateScriptInputTopic({
            topic: topic.name,
            podcastName,
            context: provideContext ? context : '',
            concept: topic.branstormConcepts
              .filter(v => v.selected)
              .map(v => v.concept),
            numberOfSections,
            numberOfGuests,
          })
          .pipe(
            map(data => ({
              ...data,
              inputParams: {
                context: context ?? undefined,
                guests: numberOfGuests,
                sections: numberOfSections,
                subject: topic.name,
                concept: topic.branstormConcepts.length
                  ? topic.branstormConcepts
                      .filter(v => v.selected)
                      .map(v => v.concept)
                  : undefined,
                includeResearch: true,
              } satisfies IdeaInputParams,
            })),
          );
      }
      case InputType.Text: {
        return this.podcastService
          .generateScriptInputText({
            mainSource: text.mainText,
            podcastName,
            context: provideContext ? context : '',
            numberOfSections,
            numberOfGuests,
          })
          .pipe(
            map(data => ({
              ...data,
              inputParams: {
                context: context ?? undefined,
                guests: numberOfGuests,
                sections: numberOfSections,
                mainText: text.mainText,
              } satisfies IdeaInputParams,
            })),
          );
      }
      case InputType.Sources: {
        return this.podcastService
          .generateArticleInputUrlOrText({
            inputUrlOrTextList: sources.items.filter(Boolean),
            userTextType: sources.style,
            promptLanguage: sources.language,
            context: sources.context,
          })
          .pipe(
            map(data => ({
              ...data,
              inputParams: {
                context: sources.context,
                promptLanguage: sources.language,
                topic: '',
                userTextType: sources.style,
                inputUrlOrTextList: sources.items.filter(Boolean),
              } satisfies IdeaInputParams,
            })),
          );
      }
      default:
        return throwError(() => new Error('Unknown input type'));
    }
  }

  private savePodcast(
    data: {
      inputParams: IdeaInputParams;
      script: string;
      shortTitle?: string;
      oneLiner?: string;
    } & Partial<Pick<Idea, 'outline' | 'evidence'>>,
  ) {
    this.busService.publish(
      this.eventsService.requested.action.session.idea.create,
      {
        ...this.initMorphoxes(),
        title: (
          data.shortTitle ||
          this.podcastTitle ||
          this.podcastName ||
          ''
        ).substring(0, 45),
        one_liner: data.oneLiner,
        inputParams: data.inputParams,
        categories: [this.podcastName],
        outline: data.outline,
        textScript: { original: { value: data.script, v: 0 } },
        evidence: data.evidence,
        published: true,
      },
      this.session,
    );
    return new Observable<Idea>(observer => {
      this.busService.subscribe(
        this.eventsService.received.action.session.idea.create.success,
        (idea: Idea) => (observer.next(idea), observer.complete()),
      );
    });
  }

  /**
   * Note (Tim): Has anyone thought of performance implications? How does this affect performance
   * when a session has many morphboxes? Is there a way to handle this more efficiently (or maybe offload it to the server)?
   */
  private initMorphoxes(): Pick<Idea, 'morphboxes'> {
    const morphboxes = [];

    for (const morphbox of this.session.data.morphboxes) {
      let morphboxAdded = false;

      for (const row of morphbox.rows) {
        let rowAdded = false;

        for (const element of row.elements) {
          if (element.clicked) {
            if (!morphboxAdded) {
              morphboxes.push({
                name: morphbox.name,
                rows: [],
              });
              morphboxAdded = true;
            }
            if (!rowAdded) {
              morphboxes[morphboxes.length - 1].push({
                text: row.text,
                elements: [],
              });
              rowAdded = true;
            }
            const currentMorphbox = morphboxes[morphboxes.length - 1];
            currentMorphbox.values[currentMorphbox.values.length - 1].push(
              element.text,
            );
          }
        }
      }
    }

    return { morphboxes };
  }
}
